import { useMemo, useState } from 'react'
import { FgHelp } from '../help/FgHelp'
import { CATALOG, ROLE_LABELS, ROLE_ORDER, groupForRole, promptForAssistant, type Catalog, type MinRole } from './catalog'
import { copyText } from './connectApi'

export type CapabilityBrowserProps = {
  catalog?: Catalog
  /** The viewer's role; the browser only offers what that role can run. */
  role?: MinRole
  /** Where "Run in the sidebar" goes. In the Excel pane this invokes directly; on the web it copies for the pane. */
  onRunInSidebar?: (request: string) => void
}

/** "What can I ask": every catalog example the viewer can use, grouped by job, ready to copy or run. */
export function CapabilityBrowser({ catalog = CATALOG, role: initialRole = 'consultant_owner', onRunInSidebar }: CapabilityBrowserProps) {
  const [role, setRole] = useState<MinRole>(initialRole)
  const [query, setQuery] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const groups = useMemo(() => groupForRole(catalog, role, query), [catalog, role, query])

  const copyFor = async (request: string) => {
    setNotice(await copyText(promptForAssistant(request)) ? 'Copied. Paste it into Claude or ChatGPT with FuseGrid connected.' : 'Copy failed. Select the text and copy it yourself.')
  }
  const runInSidebar = async (request: string) => {
    if (onRunInSidebar) { onRunInSidebar(request); return }
    setNotice(await copyText(request) ? 'Copied. Open FuseGrid in Excel and paste it into the assistant.' : 'Copy failed. Select the text and copy it yourself.')
  }

  return <section className="fg-connect-card fg-ask" aria-labelledby="fg-ask-title">
    <div className="fg-connect-card-head">
      <h2 id="fg-ask-title">What can I ask?<FgHelp id="capabilities.browser" /></h2>
      <p>Real requests FuseGrid answers, grouped by the job you are doing. Copy one into Claude or ChatGPT, or run it in the Excel sidebar.</p>
    </div>
    <div className="fg-ask-filters">
      <label>Show what I can do as
        <select value={role} onChange={event => setRole(event.target.value as MinRole)}>
          {ROLE_ORDER.map(value => <option key={value} value={value}>{ROLE_LABELS[value]}</option>)}
        </select>
      </label>
      <label>Search
        <input type="search" value={query} placeholder="cash, board pack, variance…" onChange={event => setQuery(event.target.value)} />
      </label>
      <FgHelp id="capabilities.role_filter" />
    </div>
    {notice && <p className="fg-connect-notice" role="status">{notice}</p>}
    {groups.length === 0 && <p className="fg-connect-muted">Nothing matches. Try a different word, or a broader role.</p>}
    {groups.map(group => <div className="fg-ask-job" key={group.job}>
      <h3>{group.title}</h3>
      {group.entries.map(entry => <article className="fg-ask-entry" key={`${group.job}:${entry.name}`} data-capability={entry.name}>
        <h4>{entry.title}<FgHelp id={entry.help_id} /></h4>
        <p>{entry.summary}</p>
        <ul>
          {entry.examples.map(example => <li key={example.request}>
            <q>{example.request}</q>
            <span className="fg-ask-actions">
              <button type="button" onClick={() => void copyFor(example.request)}>Copy for Claude/ChatGPT</button>
              {entry.sidebar !== 'hidden' && <button type="button" onClick={() => void runInSidebar(example.request)}>Run in the sidebar</button>}
            </span>
          </li>)}
        </ul>
      </article>)}
    </div>)}
  </section>
}
